import { ListFilter, Search, X } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { cx } from '@/lib/cx';
import { useElementWidth } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { Button } from './Button';
import s from './FilterBar.module.css';
import { Sheet } from './Sheet';

export interface FilterBarProps {
  /** Free-text search, debounced; bind to `?q=`. */
  search?: { value: string | undefined; onChange: (q: string | undefined) => void; placeholder?: string };
  /** Filter controls (Select, Combobox…), each bound to a search param by the page. */
  children?: ReactNode;
  activeCount?: number;
  onClear?: () => void;
  /** Right-aligned page actions (e.g. "Add vehicle"). */
  actions?: ReactNode;
  /** Summary on the right (e.g. "124 vehicles"). */
  summary?: ReactNode;
  className?: string;
}

/** URL-bound filters. Below 640 px of container width the filters move into a sheet. */
export function FilterBar({ search, children, activeCount = 0, onClear, actions, summary, className }: FilterBarProps) {
  const [ref, width] = useElementWidth();
  const narrow = width > 0 && width < 640;
  const [open, setOpen] = useState(false);

  return (
    <div ref={ref} className={cx(s.bar, className)} role="search">
      {search && <SearchBox {...search} />}
      {narrow ? (
        children && (
          <>
            <Button icon={ListFilter} onClick={() => setOpen(true)} className={s.filtersButton}>
              {t('Filters')}
              {activeCount > 0 && <span className={s.count}>{activeCount}</span>}
            </Button>
            <Sheet
              open={open}
              onOpenChange={setOpen}
              title={t('Filters')}
              side="bottom"
              footer={
                <>
                  {onClear && activeCount > 0 && (
                    <Button variant="ghost" onClick={onClear}>
                      {t('Clear all')}
                    </Button>
                  )}
                  <Button variant="primary" onClick={() => setOpen(false)}>
                    {t('Done')}
                  </Button>
                </>
              }
            >
              <div className={s.sheetFilters}>{children}</div>
            </Sheet>
          </>
        )
      ) : (
        <div className={s.filters}>
          {children}
          {onClear && activeCount > 0 && (
            <Button variant="ghost" size="sm" icon={X} onClick={onClear}>
              {t('Clear')}
            </Button>
          )}
        </div>
      )}
      {(summary || actions) && (
        <div className={s.end}>
          {summary && <span className={s.summary}>{summary}</span>}
          {actions}
        </div>
      )}
    </div>
  );
}

function SearchBox({ value, onChange, placeholder }: NonNullable<FilterBarProps['search']>) {
  const [text, setText] = useState(value ?? '');
  const last = useRef(value ?? '');
  // Follow the URL when it changes from elsewhere (back button, clear all).
  useEffect(() => {
    if ((value ?? '') !== last.current) {
      last.current = value ?? '';
      setText(value ?? '');
    }
  }, [value]);
  useEffect(() => {
    if (text === last.current) return;
    const id = setTimeout(() => {
      last.current = text;
      onChange(text.trim() || undefined);
    }, 300);
    return () => clearTimeout(id);
  }, [text, onChange]);
  return (
    <label className={s.search}>
      <Search className={s.searchIcon} aria-hidden />
      <span className="sr-only">{placeholder ?? t('Search')}</span>
      <input type="search" className={s.searchInput} value={text} placeholder={placeholder ?? t('Search…')} onChange={(e) => setText(e.target.value)} />
      {text && (
        <button type="button" className={s.clearSearch} aria-label={t('Clear search')} onClick={() => setText('')}>
          <X aria-hidden />
        </button>
      )}
    </label>
  );
}
